import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/core";

const ArrowGrid = ({ config }) => {
  const {
    gridTemplate,
    data,
    rowPositions,
    colors,
    helpers,
    showPriceList = true,
  } = config;

  // Determine responsive grid template based on Price List visibility
  const getResponsiveGridTemplate = () => {
    if (showPriceList) {
      return gridTemplate.columns || "80px 1fr 140px 1fr 1fr 1fr";
    } else {
      // When Price List is hidden, remove first column
      return "1fr 140px 1fr 1fr 1fr";
    }
  };

  // Early return if no data or invalid structure
  if (!data || !data.topologySubsections || !rowPositions) {
    return <div>Loading arrow grid...</div>;
  }

  // Use dynamic color palette from configuration
  const colorPalette = colors || [
    "#2196f3",
    "#4caf50",
    "#ff9800",
    "#9c27b0",
    "#f44336",
    "#00bcd4",
    "#795548",
    "#607d8b",
  ];

  const useStyles = makeStyles(() => ({
    grid: {
      display: "grid",
      width: "100%",
      minHeight: "200px",
      gap: "12px 0px", // Remove column gaps for more space
    },
    arrowTrack: {
      position: "relative",
      display: "flex",
      alignItems: "center",
      height: "100%",
      background: "transparent", // Remove the blue gradient background
      borderRadius: "2px",
      overflow: "visible", // Allow labels to show
    },
    arrowLine: {
      position: "absolute",
      top: "50%",
      transform: "translateY(-50%)",
      borderRadius: "2px",
    },
    arrowHead: {
      position: "absolute",
      right: "-13px",
      top: "50%",
      transform: "translateY(-50%)",
      width: 0,
      height: 0,
    },
  }));

  const classes = useStyles();

  const gridStyle = {
    gridTemplateRows: gridTemplate.rows,
    gridTemplateColumns: getResponsiveGridTemplate(),
  };

  // Create arrow tracks using new configuration system
  const createArrowTracks = () => {
    const tracks = [];

    // Use configuration-based row positions
    rowPositions.forEach((rowConfig) => {
      const subsection = data.topologySubsections[rowConfig.subsectionIndex];

      // Add validation to prevent errors
      if (!subsection || !subsection.tracks) {
        console.warn(
          `Missing subsection at index ${rowConfig.subsectionIndex}`,
        );
        return;
      }

      const track = subsection.tracks[rowConfig.trackIndex];

      if (!track) {
        console.warn(
          `Missing track at index ${rowConfig.trackIndex} in subsection ${rowConfig.subsectionIndex}`,
        );
        return;
      }

      // Get color using helper function or fallback to palette
      const arrowColor = helpers?.getColorForSubsection
        ? helpers.getColorForSubsection(rowConfig.subsectionIndex)
        : colorPalette[rowConfig.subsectionIndex % colorPalette.length];

      // Use the individual row height for dynamic arrow sizing
      const dynamicArrowHeight = Math.min(rowConfig.height * 0.8, 120); // 80% of row height, max 120px

      const trackStyle = {
        gridRow: rowConfig.row,
        gridColumn: "1 / -1", // Span all columns for proper positioning
      };

      // Calculate arrow positioning dynamically based on Price List visibility
      // Arrow leftmost edge should always be at the CENTER of Step 1 boxes
      const columnPositions = helpers?.getColumnPosition || null;
      let arrowLeftPosition;
      let arrowRightPosition = "15.25%"; // Default right position

      if (columnPositions) {
        // Use helper for precise positioning if available
        const step1Pos = columnPositions("step1");
        const step5Pos = columnPositions("step5");

        if (step1Pos) {
          // Calculate center of Step 1 column
          const step1Center = (step1Pos.start + step1Pos.end) / 2;
          arrowLeftPosition = `${step1Center}%`;
        } else {
          // Fallback based on Price List visibility
          arrowLeftPosition = showPriceList ? "15.5%" : "10%";
        }

        if (step5Pos) {
          arrowRightPosition = `${100 - step5Pos.start}%`; // End at start of step5
        }
      } else {
        // Fallback positioning when no helper available
        // Calculate approximate center of Step 1 based on grid layout
        if (showPriceList) {
          // Grid: 80px | 1fr | 140px | 1fr | 1fr | 1fr
          // Step 1 is in column 2 (1fr), approximate center is around 15.5%
          arrowLeftPosition = "15.5%";
        } else {
          // Grid: 1fr | 140px | 1fr | 1fr | 1fr
          // Step 1 is in column 1 (1fr), approximate center is around 10%
          arrowLeftPosition = "10%";
        }
      }

      tracks.push(
        <div
          key={`arrow-${track.id}`}
          className={classes.arrowTrack}
          style={trackStyle}
        >
          <div
            className={classes.arrowLine}
            style={{
              left: arrowLeftPosition, // Dynamic start position
              right: arrowRightPosition, // Dynamic end position
              height: `${dynamicArrowHeight}px`,
              backgroundColor: arrowColor,
            }}
          >
            <div
              className={classes.arrowHead}
              style={{
                borderLeft: `14px solid ${arrowColor}`, // Dynamic color
                borderTop: `${dynamicArrowHeight / 2}px solid transparent`, // Dynamic size
                borderBottom: `${dynamicArrowHeight / 2}px solid transparent`, // Dynamic size
              }}
            />
          </div>
        </div>,
      );
    });

    return tracks;
  };

  return (
    <div className={classes.grid} style={gridStyle}>
      {createArrowTracks()}
    </div>
  );
};

ArrowGrid.propTypes = {
  config: PropTypes.shape({
    rows: PropTypes.number.isRequired,
    columns: PropTypes.number.isRequired,
    gridTemplate: PropTypes.shape({
      rows: PropTypes.string.isRequired,
      columns: PropTypes.string.isRequired,
      responsive: PropTypes.shape({
        desktop: PropTypes.string,
        tablet: PropTypes.string,
        mobile: PropTypes.string,
      }),
    }).isRequired,
    colors: PropTypes.arrayOf(PropTypes.string).isRequired,
    helpers: PropTypes.shape({
      getColorForSubsection: PropTypes.func.isRequired,
      getColumnPosition: PropTypes.func.isRequired,
    }).isRequired,
    rowPositions: PropTypes.arrayOf(
      PropTypes.shape({
        subsectionName: PropTypes.string.isRequired,
        subsectionIndex: PropTypes.number.isRequired,
        trackIndex: PropTypes.number.isRequired,
        row: PropTypes.number.isRequired,
        isFirstInSubsection: PropTypes.bool.isRequired,
        isLastInSubsection: PropTypes.bool.isRequired,
      }),
    ).isRequired,
    data: PropTypes.shape({
      topologySubsections: PropTypes.arrayOf(
        PropTypes.shape({
          name: PropTypes.string.isRequired,
          tracks: PropTypes.arrayOf(
            PropTypes.shape({
              id: PropTypes.string.isRequired,
              label: PropTypes.string.isRequired,
            }),
          ).isRequired,
        }),
      ).isRequired,
    }).isRequired,
    showPriceList: PropTypes.bool,
  }).isRequired,
};

export default ArrowGrid;
